import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { LuClock, LuChevronUp, LuChevronDown } from "react-icons/lu";
import "./ModernDateTimePicker.css";

const QUICK_SUGGESTIONS = ["09:00 AM", "12:00 PM", "03:00 PM", "06:00 PM"];

export default function ModernTimePicker({
  value,
  onChange,
  minTime,
  maxTime,
  placeholder = "hh:mm A",
  disabled = false,
  onDisabledClick,
  error = false,
  className = "",
  label,
  id,
  format = "hh:mm A",
  relatedDate,
  relatedStartTime,
  relatedEndTime,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Normalize current value to a moment object
  const getMomentFromValue = (val) => {
    if (!val) return null;
    if (moment.isMoment(val)) return val.isValid() ? val : null;
    const parsed = moment(val, ["HH:mm", "HH:mm:ss", "hh:mm A", "h:mm A", "hh:mm a", "h:mm a", "YYYY-MM-DD HH:mm"]);
    return parsed.isValid() ? parsed : null;
  };

  const selectedMoment = getMomentFromValue(value);

  // Default fallback time (current time + 5 mins, rounded to 5 mins)
  const getDefaultTime = () => {
    if (minTime && moment.isMoment(minTime) && minTime.isValid()) {
      return minTime.clone();
    }
    return moment().add(5, "minutes");
  };

  // Internal time state (Hour 1-12, Minute 0-59, Period AM/PM)
  const initialBase = selectedMoment || getDefaultTime();
  const [hour, setHour] = useState(() => {
    const h = initialBase.hour() % 12;
    return h === 0 ? 12 : h;
  });
  const [minute, setMinute] = useState(() => initialBase.minute());
  const [period, setPeriod] = useState(() => (initialBase.hour() >= 12 ? "PM" : "AM"));

  // Sync internal state when `value` or `isOpen` changes
  useEffect(() => {
    const base = getMomentFromValue(value) || getDefaultTime();
    const h = base.hour() % 12;
    setHour(h === 0 ? 12 : h);
    setMinute(base.minute());
    setPeriod(base.hour() >= 12 ? "PM" : "AM");
  }, [value, isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Build current pending moment from hour, minute, period
  const getPendingMoment = () => {
    let h24 = hour % 12;
    if (period === "PM") h24 += 12;
    return moment().hour(h24).minute(minute).second(0);
  };

  const pendingMoment = getPendingMoment();

  // Helper to test if a given moment is before minTime or after maxTime
  const isTimeInvalid = (timeM) => {
    if (!timeM) return false;
    if (minTime && moment.isMoment(minTime) && minTime.isValid()) {
      const minHour = minTime.hour();
      const minMinute = minTime.minute();
      if (timeM.hour() < minHour || (timeM.hour() === minHour && timeM.minute() < minMinute)) {
        return true;
      }
    }
    if (maxTime && moment.isMoment(maxTime) && maxTime.isValid()) {
      const maxHour = maxTime.hour();
      const maxMinute = maxTime.minute();
      if (timeM.hour() > maxHour || (timeM.hour() === maxHour && timeM.minute() > maxMinute)) {
        return true;
      }
    }
    return false;
  };

  // Hour Steppers
  const incrementHour = (e) => {
    e.stopPropagation();
    setHour((prev) => (prev === 12 ? 1 : prev + 1));
  };

  const decrementHour = (e) => {
    e.stopPropagation();
    setHour((prev) => (prev === 1 ? 12 : prev - 1));
  };

  // Minute Steppers (increments of 5 mins)
  const incrementMinute = (e) => {
    e.stopPropagation();
    setMinute((prev) => {
      const next = prev + 1;
      return next >= 60 ? 0 : next;
    });
  };

  const decrementMinute = (e) => {
    e.stopPropagation();
    setMinute((prev) => {
      const next = prev - 1;
      return next < 0 ? 55 : next;
    });
  };

  // Toggle AM / PM
  const handleSetPeriod = (newPeriod, e) => {
    e.stopPropagation();
    setPeriod(newPeriod);
  };

  // Quick suggestion chips click
  const handleChipClick = (suggestionStr, e) => {
    e.stopPropagation();
    const parsed = moment(suggestionStr, "hh:mm A");
    if (parsed.isValid()) {
      const h = parsed.hour() % 12;
      setHour(h === 0 ? 12 : h);
      setMinute(parsed.minute());
      setPeriod(parsed.hour() >= 12 ? "PM" : "AM");
    }
  };

  const handleTriggerClick = (e) => {
    e.preventDefault();
    if (disabled) {
      if (onDisabledClick) onDisabledClick();
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handleApply = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(pendingMoment);
    }
    setIsOpen(false);
  };

  const handleCancel = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsOpen(false);
  };

  // Live duration calculation if related start/end time is passed
  let durationDisplay = "";
  if (relatedStartTime) {
    const startM = getMomentFromValue(relatedStartTime);
    if (startM && startM.isValid()) {
      let endH24 = hour % 12;
      if (period === "PM") endH24 += 12;
      const endM = moment().hour(endH24).minute(minute).second(0);
      const diffMins = endM.diff(moment().hour(startM.hour()).minute(startM.minute()), "minutes");
      if (diffMins > 0) {
        const hrs = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        durationDisplay = `${hrs > 0 ? `${hrs} hr ` : ""}${mins > 0 ? `${mins} min` : ""}`.trim();
      }
    }
  }

  // Display summary line
  const formattedTimeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
  const datePrefix = relatedDate && moment(relatedDate).isValid() ? `${moment(relatedDate).format("ddd, MMM D")} · ` : "";
  const displaySummary = `${datePrefix}${formattedTimeStr}`;

  // Formatted trigger value
  const displayValue = selectedMoment ? selectedMoment.format(format) : "";

  return (
    <div className={`position-relative w-100 ${className}`} ref={containerRef} id={id}>
      {label && <label className="agenda-drawer-label mb-1 d-block">{label}</label>}

      {/* Input Trigger Box */}
      <div
        className={`mdtp-trigger-box ${error ? "has-error" : ""} ${disabled ? "is-disabled" : ""} ${isOpen ? "is-focused" : ""}`}
        onClick={handleTriggerClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        <span className={`mdtp-trigger-value ${!displayValue ? "is-placeholder" : ""}`}>
          {displayValue || placeholder}
        </span>
        <span className="mdtp-trigger-icon">
          <LuClock />
        </span>
      </div>

      {/* Popup Modal with Backdrop Overlay via Portal */}
      {isOpen &&
        createPortal(
          <div className="mdtp-modal-overlay" onClick={handleCancel}>
            <div
              className="mdtp-modal-dialog"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header: Select time */}
              <div className="mdtp-header">
                <span className="mdtp-header-title">Select time</span>
              </div>

              {/* Selected Time Summary & Duration Badge */}
              <div className="mdtp-summary-strip">
                <div className="mdtp-summary-content">
                  <LuClock className="mdtp-summary-icon" size={14} />
                  <span className="mdtp-summary-text">{displaySummary}</span>
                </div>
                {durationDisplay && (
                  <span className="mdtp-duration-badge">{durationDisplay}</span>
                )}
              </div>

              {/* Digital Time Selector Display */}
              <div className="mdtp-time-selector">
                <div className="mdtp-digital-clock-display">
                  {/* Hour Stepper Box */}
                  <div className="mdtp-stepper-unit">
                    <span className="mdtp-stepper-label">Hour</span>
                    <div className="mdtp-stepper-box">
                      <button
                        type="button"
                        className="mdtp-stepper-arrow"
                        onClick={incrementHour}
                        title="Increase hour"
                      >
                        <LuChevronUp size={15} />
                      </button>
                      <div className="mdtp-stepper-value-wrap">
                        <span className="mdtp-stepper-value-text">
                          {String(hour).padStart(2, "0")}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="mdtp-stepper-arrow"
                        onClick={decrementHour}
                        title="Decrease hour"
                      >
                        <LuChevronDown size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Colon Separator */}
                  <div className="mdtp-time-colon-wrap">
                    <span className="mdtp-time-colon">:</span>
                  </div>

                  {/* Minute Stepper Box */}
                  <div className="mdtp-stepper-unit">
                    <span className="mdtp-stepper-label">Minute</span>
                    <div className="mdtp-stepper-box">
                      <button
                        type="button"
                        className="mdtp-stepper-arrow"
                        onClick={incrementMinute}
                        title="Increase minute"
                      >
                        <LuChevronUp size={15} />
                      </button>
                      <div className="mdtp-stepper-value-wrap">
                        <span className="mdtp-stepper-value-text">
                          {String(minute).padStart(2, "0")}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="mdtp-stepper-arrow"
                        onClick={decrementMinute}
                        title="Decrease minute"
                      >
                        <LuChevronDown size={15} />
                      </button>
                    </div>
                  </div>

                  {/* AM / PM Segmented Control */}
                  <div className="mdtp-stepper-unit">
                    <span className="mdtp-stepper-label">Period</span>
                    <div className="mdtp-period-segment">
                      <button
                        type="button"
                        className={`mdtp-period-btn ${period === "AM" ? "is-active" : ""}`}
                        onClick={(e) => handleSetPeriod("AM", e)}
                      >
                        AM
                      </button>
                      <button
                        type="button"
                        className={`mdtp-period-btn ${period === "PM" ? "is-active" : ""}`}
                        onClick={(e) => handleSetPeriod("PM", e)}
                      >
                        PM
                      </button>
                    </div>
                  </div>
                </div>

                {/* Quick Time Suggestion Chips */}
                <div className="mdtp-suggestions-wrap">
                  <span className="mdtp-suggestions-title">Quick Suggestions</span>
                  <div className="mdtp-chips-grid">
                    {QUICK_SUGGESTIONS.map((chipStr) => {
                      const chipMoment = moment(chipStr, "hh:mm A");
                      const isChipDisabled = isTimeInvalid(chipMoment);
                      const isChipActive = formattedTimeStr === chipStr;

                      return (
                        <button
                          key={chipStr}
                          type="button"
                          className={`mdtp-chip-btn ${isChipActive ? "is-active" : ""}`}
                          onClick={(e) => handleChipClick(chipStr, e)}
                          disabled={isChipDisabled}
                        >
                          {chipStr}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer Action Buttons */}
              <div className="mdtp-footer">
                <button
                  type="button"
                  className="mdtp-btn-cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mdtp-btn-apply"
                  onClick={handleApply}
                  disabled={isTimeInvalid(pendingMoment)}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
